import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, IconButton, Typography } from '@mui/material'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import React, { useEffect, useMemo, useState } from 'react'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import useSandreList from 'utils/customHook/useSandreList'
import { orderBy, uniqBy, isNil, isNaN, keys, groupBy, compact } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getDocumentTypeIcon, getFileExtension, getFileName, getFileNatureLabel, getFileType, getFormatedFileName } from 'utils/FileUtils'
import { getDate, getYear, getYearOrString } from 'utils/DateUtil'
import useBoolean from 'utils/customHook/useBoolean'
import ProgressBar from 'components/progress/ProgressBar'
import SimpleTabList from 'components/list/SimpleTabList'
import useTitle from 'utils/customHook/useTitle'
import { PICTURE, PICTURE_TYPES } from 'components/file/constant/FileConstants'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import EditIcon from '@mui/icons-material/Edit'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import Fancybox from 'components/fancybox/Fancybox'
import { getI18nTitleDataLength, searchAllCharacters } from 'utils/StringUtil'
import useActions from 'utils/customHook/useActions'
import { getStationArrowNav } from 'utils/ActionUtils'
import { push } from '@lagunovsky/redux-react-router'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Checkbox from 'components/forms/Checkbox'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import moment from 'moment'
import StationAction from 'station/actions/StationAction'
import ToastrAction from 'toastr/actions/ToastrAction'
import ConfirmModal from 'components/modal/ConfirmModal'
import WaitAction from 'wait/WaitAction'
import PropTypes from 'prop-types'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import sandre from 'assets/pictures/logos/sandre.png'
import mtes from 'assets/pictures/logos/mtes.png'

const DATE = 'date'
const FILE = 'file'
const ACTIVE = 'active'
const IS_ACTIVE = '1'
const IS_INACTIVE = '0'

const getFileInfo = (file = {}) => {
    if (file.fileType === PICTURE) {
        return {
            sandreConstant: SANDRE.TYPE_PHOTO,
            fileTypeName: i18n.pictureType,
            image: <img src={file.url} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '4px' }} />,
            textAlign: 'left',
        }
    }
    const ext = getFileExtension(file.name)
    return {
        sandreConstant: SANDRE.TYPE_DOCUMENT,
        fileTypeName: i18n.documentType,
        image: <img src={getDocumentTypeIcon(ext)} style={{ width: '40%' }} />,
        textAlign: 'center',
    }
}

const FilterFields = ({
    defaultFilter = {},
    onValidate = () => {},
}) => {
    const {
        files,
        pictures,
    } = useSelector(store => ({
        files: store.StationReducer.files,
        pictures: store.StationReducer.pictures,
    }), shallowEqual)

    const typePictures = useSandreList(SANDRE.TYPE_PHOTO)
    const typeDocuments = useSandreList(SANDRE.TYPE_DOCUMENT)

    const [searchValue, setSearchValue] = useState(defaultFilter.searchValue)
    const [extension, setExtension] = useState(defaultFilter.extension)
    const [naturePicture, setNaturePicture] = useState(defaultFilter.naturePicture)
    const [natureDocument, setNatureDocument] = useState(defaultFilter.natureDocument)
    const [year, setYear] = useState(defaultFilter.year)
    const [active, setActive] = useState(defaultFilter.active)

    const activeOptions = useMemo(() => [
        { name: i18n.active, code: IS_ACTIVE },
        { name: i18n.inactive, code: IS_INACTIVE },
    ], [])

    const orderedTypePictures = useMemo(() => orderBy(typePictures, 'code'), [typePictures])
    const orderedTypeDocuments = useMemo(() => orderBy(typeDocuments, 'code'), [typeDocuments])

    const onSearch = () => {
        onValidate({
            searchValue,
            extension,
            naturePicture,
            natureDocument,
            year,
            active,
        })
    }

    const onReset = () => {
        setSearchValue('')
        setExtension()
        setNaturePicture()
        setNatureDocument()
        setYear()
        setActive()

        onValidate({ searchValue: '' })
    }

    const mixedFiles = useMemo(() => [...files, ...pictures], [files, pictures])

    const extensions = useMemo(() => uniqBy(mixedFiles.map(f => {
        const ext = getFileExtension(f.name)
        return { id: ext, label: ext }
    }), 'id'), [mixedFiles])

    const years = useMemo(() => {
        const formattedYears = mixedFiles.map(f => {
            if (isNil(f.date) || isNaN(f.date)) return { id: -1, label: i18n.unknownDate }
            const y = getYear(f.date)
            return { id: y, label: `${y}` }
        })
        return orderBy(uniqBy(formattedYears, 'id'), 'id')
    }, [mixedFiles])

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={2}>
                        <Input
                            title={i18n.search}
                            value={searchValue}
                            onChange={setSearchValue}
                            onEnterKeyPress={onSearch}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.extensions}
                            options={extensions}
                            value={extension}
                            onChange={setExtension}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.pictureType}
                            options={orderedTypePictures}
                            value={naturePicture}
                            onChange={setNaturePicture}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.documentType}
                            options={orderedTypeDocuments}
                            value={natureDocument}
                            onChange={setNatureDocument}
                            keyValue='name'
                            keyLabel='name'
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.year}
                            options={years}
                            value={year}
                            onChange={setYear}
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.active}
                            options={activeOptions}
                            value={active}
                            onChange={setActive}
                            nullLabel='&nbsp;'
                        />
                    </Grid>
                    <Grid size={1} offset={10}>
                        <Button
                            onClick={onReset}
                            variant='outlined'
                            color='primary'
                            fullWidth
                        >
                            {i18n.reinit}
                        </Button>
                    </Grid>
                    <Grid size={1}>
                        <Button
                            onClick={onSearch}
                            variant='contained'
                            color='primary'
                            fullWidth
                        >
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FilterFields.propTypes = {
    defaultFilter: PropTypes.shape({}),
    onValidate: PropTypes.func,
}

const ImportDialog = ({
    isOpen = false,
    onClose = () => {},
}) => {
    const dispatch = useDispatch()
    const {
        catchment,
        files,
        pictures,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        files: store.StationReducer.files,
        pictures: store.StationReducer.pictures,
    }), shallowEqual)

    const [name, setName] = useState()
    const [content, setContent] = useState()

    const [fileType, setFileType] = useState(0)
    const [date, setDate] = useState()
    const [active, setActive] = useState()

    useEffect(() => {
        if (isOpen) return

        setName()
        setContent()
        setFileType(0)
        setDate()
        setActive()
    }, [isOpen])

    const onImportFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        setName(file.name)
        reader.onload = upload => setContent(upload.target.result)
        reader.readAsDataURL(file)
    }

    const mixedFiles = useMemo(() => [...files, ...pictures], [files, pictures])

    const { sandreCode, title } = getFileType(content)
    const fileTypes = useSandreList(sandreCode)

    const saveFile = () => {
        const now = moment().valueOf()
        const activeInt = active ? parseInt(IS_ACTIVE) : parseInt(IS_INACTIVE)
        const ft = fileTypes.find(t => t.code === fileType)
        const formattedName = getFormatedFileName(catchment.code, activeInt, getFileName(name), getFileExtension(name), ft.mnemonique || ft.name, date ?? now, STATION_TYPE_NAME.catchment)
        if (mixedFiles.some(f => f.name === formattedName)) {
            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
            return
        }

        const formattedFile = {
            stationType: 11, // catchment
            stationId: catchment.id,
            path: '',
            content,
            fileType,
            active: activeInt,
            date: date ?? now,
            name: formattedName,
        }

        const type = content.includes(PICTURE_TYPES.IMAGE) ? 'picture' : 'doc'

        dispatch(WaitAction.waitStart())
        dispatch(StationAction.addStationFile(formattedFile, type)).then(() => {
            Promise.all([
                dispatch(StationAction.fetchFiles(catchment.code, STATION_TYPE_NAME.catchment)),
                dispatch(StationAction.fetchPictures(catchment.code, STATION_TYPE_NAME.catchment)),
            ]).finally(() => {
                dispatch(WaitAction.waitStop())
            })
        }).finally(onClose)
    }

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DefaultDialogTitle
                title={i18n.importFile}
                onClose={onClose}
            />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container rowSpacing={0.5} columnSpacing={1} alignItems='center'>
                            <Grid size='auto'>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    component='label'
                                    fullWidth
                                >
                                    <span>{i18n.chooseFile}</span>
                                    <input
                                        type='file'
                                        hidden
                                        onChange={onImportFile}
                                    />
                                </Button>
                            </Grid>
                            <Grid size='grow'>
                                <Input value={name ?? i18n.pleaseSelectFile} style={{ marginTop: '-0.5rem' }} disabled/>
                            </Grid>
                            {!isNil(name) && (
                                <>
                                    <Grid size={12}>
                                        <Select
                                            label={title}
                                            options={fileTypes}
                                            value={fileType}
                                            onChange={setFileType}
                                            noNullValue
                                            noSort
                                            keyLabel='name'
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <SimpleDatePicker
                                            label={i18n.date}
                                            value={date}
                                            onChange={setDate}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <Checkbox
                                            onChange={setActive}
                                            checked={active}
                                            label={i18n.active}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={saveFile}
                    variant='contained'
                    color='primary'
                    disabled={isNil(content) || isNil(name) || isNil(fileType)}
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ImportDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
}

const UpdateFileDialog = ({
    isOpen = false,
    onClose = () => {},

    fileName,
}) => {
    const dispatch = useDispatch()
    const {
        catchment,
        files,
        pictures,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        files: store.StationReducer.files,
        pictures: store.StationReducer.pictures,
    }), shallowEqual)

    const [name, setName] = useState()
    const [fileType, setFileType] = useState(0)
    const [date, setDate] = useState()
    const [active, setActive] = useState()

    const mixedFiles = useMemo(() => [...files, ...pictures], [files, pictures])
    const file = useMemo(() => mixedFiles.find(f => f.name === fileName), [mixedFiles, fileName])

    const {
        sandreConstant,
        fileTypeName,
    } = getFileInfo(file)

    const fileTypes = useSandreList(sandreConstant)

    useEffect(() => {
        if (isNil(file)) return

        const ft = fileTypes.find(t => t.mnemonique === file.type || t.name === file.type)?.code

        setName(file.shortName)
        setFileType(ft)
        setDate(file.date)
        setActive(file.active === IS_ACTIVE)
    }, [file])

    const saveFile = () => {
        const now = moment().valueOf()
        const activeInt = active ? parseInt(IS_ACTIVE) : parseInt(IS_INACTIVE)
        const ft = fileTypes.find(t => t.code === fileType)
        const formattedName = getFormatedFileName(catchment.code, activeInt, name, getFileExtension(file.name), ft.mnemonique || ft.name, date ?? now, STATION_TYPE_NAME.catchment)
        if (file.name !== formattedName && mixedFiles.some(f => f.name === formattedName)) {
            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
            return
        }

        const formattedFile = {
            ...file,
            stationType: 'CATCHMENT',
            newName: formattedName,
            fileExtension: getFileExtension(file.name),
            newType: fileType,
            fileTypeName: ft.mnemonique || ft.name,
            newDate: date ?? now,
            displayName: file.isAllDataName && catchment?.code === file.stationCode ? file.shortName : getFileName(file.name),
        }

        dispatch(WaitAction.waitStart())
        dispatch(StationAction.updateFile(formattedFile)).then(() => {
            Promise.all([
                dispatch(StationAction.fetchFiles(catchment.code, STATION_TYPE_NAME.catchment)),
                dispatch(StationAction.fetchPictures(catchment.code, STATION_TYPE_NAME.catchment)),
            ]).finally(() => {
                dispatch(WaitAction.waitStop())
            })
        }).finally(onClose)
    }

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DefaultDialogTitle
                title={`${i18n.update}: ${file?.shortName ?? i18n.unknown}.${getFileExtension(file?.name)}`}
                onClose={onClose}
            />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container rowSpacing={0.5} columnSpacing={1} alignItems='center'>
                            <Grid size={12}>
                                <Input
                                    title={i18n.name}
                                    value={name}
                                    onChange={setName}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Select
                                    label={fileTypeName}
                                    options={fileTypes}
                                    value={fileType}
                                    onChange={setFileType}
                                    noNullValue
                                    noSort
                                    keyLabel='name'
                                />
                            </Grid>
                            <Grid size={6}>
                                <SimpleDatePicker
                                    label={i18n.date}
                                    value={date}
                                    onChange={setDate}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Checkbox
                                    onChange={setActive}
                                    checked={active}
                                    label={i18n.active}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={saveFile}
                    variant='contained'
                    color='primary'
                    disabled={isNil(name) || isNil(fileType)}
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

UpdateFileDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    fileName: PropTypes.string,
}

const FileCard = ({
    file = {},
    isEditMode = false,

    onUpdate,
    onDelete,
}) => {
    const {
        catchment,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
    }), shallowEqual)

    const displayName = file.isAllDataName && catchment?.code === file.stationCode ? file.shortName : getFileName(file.name)

    const {
        sandreConstant,
        image,
        textAlign,
    } = getFileInfo(file)

    const fileNatures = useSandreList(sandreConstant)

    return (
        <Fancybox>
            <a href={isEditMode ? undefined : file.url} target='_blank' data-fancybox={file.fileType == PICTURE && !isEditMode ? 'images' : undefined} data-caption={displayName}>
                <Card>
                    <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                            <Grid size={3} textAlign={textAlign}>
                                {image}
                            </Grid>
                            <Grid container size={9} rowSpacing={0} columnSpacing={0.2}>
                                <Grid size='grow'>
                                    <Typography
                                        style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                        fontWeight='bold'
                                        variant='subtitle1'
                                        color='text.secondary'
                                        component='div'
                                    >
                                        {displayName}
                                    </Typography>
                                </Grid>
                                {isEditMode && (
                                    <>
                                        <Grid size='auto'>
                                            <IconButton
                                                color='primary'
                                                size='small'
                                                onClick={() => onUpdate(file.name)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Grid>
                                        <Grid size='auto'>
                                            <IconButton
                                                color='primary'
                                                size='small'
                                                onClick={() => onDelete(file.name)}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </Grid>
                                    </>
                                )}
                                <Grid size={12}>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.type} : {getFileExtension(file.name)}
                                    </Typography>
                                </Grid>
                                <Grid size={12}>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.nature} : {getFileNatureLabel(file, fileNatures)}
                                    </Typography>
                                </Grid>
                                <Grid size={12}>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.date} : {getDate(file.date)}
                                    </Typography>
                                </Grid>
                                <Grid size={6} display='flex' alignItems='center'>
                                    <Typography variant='subtitle1' color='text.secondary' component='div'>
                                        {i18n.active} :
                                    </Typography>
                                    {file.active === IS_ACTIVE ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </a>
        </Fancybox>
    )
}

FileCard.propTypes = {
    file: PropTypes.shape({}),
    isEditMode: PropTypes.bool,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
}

const groupFiles = (files, group, typePictures, typeDocuments) => {
    switch (group) {
        case FILE: return groupBy(files, f => {
            const listTypes = f.fileType === PICTURE ? typePictures : typeDocuments
            return listTypes.find(t => t.mnemonique === f.type || t.name === f.type)?.name
        })
        case ACTIVE: return groupBy(files, file => file.active === IS_ACTIVE ? i18n.active : i18n.inactive)
        case DATE:
        default: return groupBy(files, file => getYearOrString(file.date, i18n.thisYear, ''))
    }
}

const CatchmentDocumentApp = ({

}) => {
    const dispatch = useDispatch()

    const {
        files,
        pictures,
        catchment,
        catchments,
    } = useSelector(store => ({
        files: store.StationReducer.files,
        pictures: store.StationReducer.pictures,
        catchment: store.CatchmentReducer.catchment,
        catchments: store.CatchmentReducer.catchments,
    }), shallowEqual)

    const typePictures = useSandreList(SANDRE.TYPE_PHOTO)
    const typeDocuments = useSandreList(SANDRE.TYPE_DOCUMENT)
    const agencies = useSandreList(SANDRE.AGENCES)

    const [filter, setFilter] = useState({ searchValue: '' })

    const [selectedFile, setSelectedFile] = useState()

    const {
        value: isLoaded,
        setTrue: onLoaded,
    } = useBoolean(false)
    const {
        value: isEditMode,
        setTrue: onEdit,
        setFalse: onRead,
    } = useBoolean(false)
    const {
        value: isNewFileOpen,
        setTrue: onNewFileOpen,
        setFalse: onNewFileClose,
    } = useBoolean(false)
    const {
        value: isUpdateFileOpen,
        setTrue: onUpdateFileOpen,
        setFalse: onUpdateFileClose,
    } = useBoolean(false)
    const {
        value: isConfirmOpen,
        setTrue: onConfirmOpen,
        setFalse: onConfirmClose,
    } = useBoolean(false)

    useEffect(() => {
        if (!catchment.code) {
            onLoaded()
            return
        }
        Promise.all([
            dispatch(StationAction.fetchFiles(catchment.code, STATION_TYPE_NAME.catchment)),
            dispatch(StationAction.fetchPictures(catchment.code, STATION_TYPE_NAME.catchment)),
        ]).finally(onLoaded)
    }, [catchment.code, dispatch])

    useTitle(() => [{
        title: i18n.catchments,
        href: 'catchment',
    }, {
        title: catchment.code ? `[${catchment.code}] ${catchment.name ?? ''}` : catchment.name || i18n.unknown,
        href: `station/catchment/${catchment.id}`,
    }, {
        title: i18n.picturesAndDocuments,
        href: `station/catchment/${catchment.id}/document`,
    }], [catchment])

    useActions(() => {
        const baseActions = {
            arrowNav: getStationArrowNav('catchment', catchments, catchment.id, s => dispatch(push(`/station/catchment/${s.id}/document`))),
        }
        if (isEditMode) {
            return {
                cancel: onRead,
                new: onNewFileOpen,
                ...baseActions,
            }
        }
        const agency = agencies.find(a => a.code === catchment.pond)
        const ref = agency?.mnemonique || agency?.reference
        const mtesLink = ref ? {
            img: mtes,
            href: `http://www.deb.developpement-durable.gouv.fr/telechargements/fiche.php?IG=${ref}_${catchment.code}`,
            label: i18n.mtes,
        } : undefined
        const sandreLink = catchment.sandreCode ? {
            img: sandre,
            href: `https://id.eaufrance.fr/AAC/${catchment.sandreCode}`,
            label: i18n.sandre,
        } : undefined
        return {
            edit: onEdit,
            ...baseActions,
            links: compact([mtesLink, sandreLink]),
        }
    }, [isEditMode, catchments, catchment])

    const mixedFiles = useMemo(() => [...files, ...pictures], [files, pictures])

    const filteredFiles = useMemo(() => {
        const {
            searchValue,
            extension,
            naturePicture,
            natureDocument,
            year,
            active,
        } = filter

        const search = searchAllCharacters(searchValue)
        const typePicture = typePictures.find(t => t.mnemonique === naturePicture || t.name === naturePicture)
        const typeDocument = typeDocuments.find(t => t.mnemonique === natureDocument || t.name === natureDocument)

        const filterByName = search ? mixedFiles.filter(f => searchAllCharacters(f.shortName).includes(search)) : mixedFiles
        const filterByActive = active ? filterByName.filter(f => f.active === active) : filterByName
        const filterByYear = year ? filterByActive.filter(f => getYear(f.date) === year) : filterByActive
        const filterByExtension = extension ? filterByYear.filter(f => getFileExtension(f.name) === extension) : filterByYear
        const filterByNaturePicture = naturePicture ? filterByExtension.filter(f => {
            return f.fileType !== PICTURE || typePicture.mnemonique === f.type || typePicture.name === f.type
        }) : filterByExtension
        const filterByNatureDocument = natureDocument ? filterByNaturePicture.filter(f => {
            return f.fileType === PICTURE || typeDocument.mnemonique === f.type || typeDocument.name === f.type
        }) : filterByNaturePicture
        return filterByNatureDocument
    }, [filter, mixedFiles, typePictures, typeDocuments])

    return (
        <Grid container style={{ paddingBottom: '100' }} spacing={1} direction='column'>
            <FilterFields
                defaultFilter={filter}
                onValidate={setFilter}
            />
            <SimpleTabList
                defaultTab={DATE}
                tabs={[
                    {
                        constant: DATE,
                        label: i18n.date,
                        icon: 'insert_invitation',
                    },
                    {
                        constant: FILE,
                        label: i18n.files,
                        icon: 'insert_drive_file',
                    },
                    {
                        constant: ACTIVE,
                        label: i18n.active,
                        icon: 'remove_red_eye',
                    },
                ]}
            >
                {
                    tab => {
                        if (!isLoaded) {
                            return (
                                <div style={{ padding: '10px' }}>
                                    <ProgressBar indeterminate withMessage />
                                </div>
                            )
                        }
                        if (!catchment.code) {
                            return (
                                <div className='center font-size-20'>{i18n.catchmentIdRequired}</div>
                            )
                        }
                        if (filteredFiles.length === 0) {
                            return (
                                <div className='center font-size-20'>{i18n.noResults}</div>
                            )
                        }
                        const groups = groupFiles(filteredFiles, tab, typePictures, typeDocuments)
                        return (
                            <Grid container spacing={1} padding='10px'>
                                {keys(groups).reverse().map((key, i) => (
                                    <Grid key={key} size={12}>
                                        <Accordion defaultExpanded={i === 0}>
                                            <AccordionSummary>
                                                {`${key} (${groups[key].length} ${getI18nTitleDataLength(i18n.element, i18n.elements, groups[key].length)})`}
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Grid container spacing={1}>
                                                    {groups[key].map(f => (
                                                        <Grid key={f.name} size={3}>
                                                            <FileCard
                                                                file={f}
                                                                isEditMode={isEditMode}
                                                                onUpdate={name => {
                                                                    setSelectedFile(name)
                                                                    onUpdateFileOpen()
                                                                }}
                                                                onDelete={name => {
                                                                    setSelectedFile(name)
                                                                    onConfirmOpen()
                                                                }}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>
                                ))}
                            </Grid>
                        )
                    }
                }
            </SimpleTabList>
            <ImportDialog isOpen={isNewFileOpen} onClose={onNewFileClose}/>
            <UpdateFileDialog isOpen={isUpdateFileOpen} onClose={onUpdateFileClose} fileName={selectedFile}/>
            <ConfirmModal
                isOpen={isConfirmOpen}
                title={i18n.deletingConfirmation}
                subTitle={selectedFile}
                onClose={onConfirmClose}
                onValidate={() => {
                    const file = mixedFiles.find(f => f.name === selectedFile)
                    dispatch(WaitAction.waitStart())
                    dispatch(StationAction.deleteFile(file)).then(() => {
                        Promise.all([
                            dispatch(StationAction.fetchFiles(catchment.code, STATION_TYPE_NAME.catchment)),
                            dispatch(StationAction.fetchPictures(catchment.code, STATION_TYPE_NAME.catchment)),
                        ]).finally(() => {
                            dispatch(WaitAction.waitStop())
                        })
                    }).finally(onConfirmClose)
                }}
            />
        </Grid>
    )
}

export default CatchmentDocumentApp